/**
 * Shogun dock-state hook — v2.
 *
 * Owns where the Shogun panel lives, whether it is open, and how the floating
 * panel is sized/positioned. The v2 rework splits persistence by the NATURE of
 * the data (app-shell ADR-024):
 *
 *   - SEMANTIC preferences — dock `mode` — persist on the backend
 *     `UserPreferences` blob (`shogun_dock_mode`), so they follow the user across devices and are
 *     observable/SDK-typed. Read straight from the TanStack Query cache and
 *     written with the shared optimistic PATCH — the exact pattern
 *     `useChatPanelLayout` uses for `chat_file_panel_position`.
 *   - GEOMETRY — the floating rect — is viewport-dependent and meaningless
 *     across devices, so it stays in localStorage under the versioned key
 *     `localagent-shogun-dock-v2`.
 *
 * First-run default is bounded FLOATING (not the shipped docked-right takeover):
 * a launcher-anchored rect clamped into the viewport below the title bar. The
 * shipped v1 keys (`localagent-shogun-dock`, `localagent-shogun-autoopen`) are
 * DELETED once on first v2 read, never migrated — the only v1 state in the wild
 * is the broken default this feature replaces (app-shell ADR-024).
 *
 * A route may SUPPLY a placement (app-shell ADR-016). An
 * agent-mediated view has no other way to act, so its route declares a bottom
 * dock and forces the panel open, and the resolver prefers that over the stored
 * preference. Open is the route's `forceOpen`; leaving the view restores the
 * user's own choice.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import { useMatches } from 'react-router-dom';

import {
  shogunFloatingRectAtom,
  shogunDockedSizeAtom,
} from '../../stores/layout/atoms';
import { useUserPreferences } from '../../hooks/useUserPreferences';

export type ShogunDockMode = 'left' | 'right' | 'top' | 'bottom' | 'floating';

export interface ShogunFloatingRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Docked-lane size: `width` applies to left/right docks, `height` to top/bottom. */
export interface ShogunDockedSize {
  width: number;
  height: number;
}

const V2_GEOMETRY_KEY = 'localagent-shogun-dock-v2';
const V1_DOCK_KEY = 'localagent-shogun-dock';
const V1_AUTOOPEN_KEY = 'localagent-shogun-autoopen';

export const TITLEBAR_H = 48; // TitleBar is h-12 (48px).
const MARGIN = 16;
const SIDEBAR_W = 220;
const DEFAULT_W = 420;
const DEFAULT_H = 560;
export const MIN_FLOATING_W = 320;
export const MIN_FLOATING_H = 240;
export const DOCKED_W = 420;
export const DOCKED_H = 360;
export const MAX_DOCKED_W = 900;
export const MAX_DOCKED_H = 700;

const DEFAULT_MODE: ShogunDockMode = 'floating';
const DOCK_MODES: ShogunDockMode[] = ['left', 'right', 'top', 'bottom', 'floating'];

function isMode(v: unknown): v is ShogunDockMode {
  return typeof v === 'string' && (DOCK_MODES as string[]).includes(v);
}

function isRect(v: unknown): v is ShogunFloatingRect {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.x === 'number' &&
    typeof r.y === 'number' &&
    typeof r.width === 'number' &&
    typeof r.height === 'number'
  );
}

function viewport(): { vw: number; vh: number } {
  if (typeof window === 'undefined') return { vw: 1280, vh: 800 };
  return { vw: window.innerWidth, vh: window.innerHeight };
}

export function defaultFloatingRect(vw: number, vh: number): ShogunFloatingRect {
  const x = Math.min(SIDEBAR_W + MARGIN, Math.max(MARGIN, vw - MIN_FLOATING_W - MARGIN));
  const width = Math.min(DEFAULT_W, vw - x - MARGIN);
  const height = Math.min(DEFAULT_H, vh - TITLEBAR_H - 2 * MARGIN);
  return { x, y: Math.max(TITLEBAR_H, vh - height - 72), width, height };
}

export function clampDockedSize(s: ShogunDockedSize): ShogunDockedSize {
  return {
    width: Math.min(Math.max(s.width, MIN_FLOATING_W), MAX_DOCKED_W),
    height: Math.min(Math.max(s.height, MIN_FLOATING_H), MAX_DOCKED_H),
  };
}

export function clampRect(r: ShogunFloatingRect, vw: number, vh: number): ShogunFloatingRect {
  const width = Math.min(Math.max(r.width, MIN_FLOATING_W), vw - 2 * MARGIN);
  const height = Math.min(Math.max(r.height, MIN_FLOATING_H), vh - TITLEBAR_H - MARGIN);
  const x = Math.min(Math.max(r.x, 0), Math.max(0, vw - width));
  const y = Math.min(Math.max(r.y, TITLEBAR_H), Math.max(TITLEBAR_H, vh - height));
  return { x, y, width, height };
}

interface GeometryV2 {
  version: 2;
  floating?: ShogunFloatingRect;
  docked?: ShogunDockedSize;
}

function isDockedSize(v: unknown): v is ShogunDockedSize {
  if (!v || typeof v !== 'object') return false;
  const s = v as Record<string, unknown>;
  return typeof s.width === 'number' && typeof s.height === 'number';
}

function readGeometryBlob(): GeometryV2 | null {
  if (typeof window === 'undefined') return null;

  if (window.localStorage.getItem(V1_DOCK_KEY) !== null ||
      window.localStorage.getItem(V1_AUTOOPEN_KEY) !== null) {
    window.localStorage.removeItem(V1_DOCK_KEY);
    window.localStorage.removeItem(V1_AUTOOPEN_KEY);
  }

  const raw = window.localStorage.getItem(V2_GEOMETRY_KEY);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const p = parsed as Record<string, unknown>;
  if (p.version !== 2) return null;
  return {
    version: 2,
    ...(isRect(p.floating) ? { floating: p.floating } : {}),
    ...(isDockedSize(p.docked) ? { docked: p.docked } : {}),
  };
}

function readGeometry(vw: number, vh: number): ShogunFloatingRect | null {
  const blob = readGeometryBlob();
  return blob?.floating ? clampRect(blob.floating, vw, vh) : null;
}

function readDockedSize(): ShogunDockedSize | null {
  const blob = readGeometryBlob();
  return blob?.docked ? clampDockedSize(blob.docked) : null;
}

// Both write paths merge into the ONE v2 blob so committing the floating rect
// never discards the docked size, and vice versa.
function writeGeometry(floating: ShogunFloatingRect): void {
  if (typeof window === 'undefined') return;
  const payload: GeometryV2 = { ...(readGeometryBlob() ?? { version: 2 }), floating };
  window.localStorage.setItem(V2_GEOMETRY_KEY, JSON.stringify(payload));
}

function writeDockedSize(docked: ShogunDockedSize): void {
  if (typeof window === 'undefined') return;
  const payload: GeometryV2 = { ...(readGeometryBlob() ?? { version: 2 }), docked };
  window.localStorage.setItem(V2_GEOMETRY_KEY, JSON.stringify(payload));
}

/**
 * A route object's `handle.shogunDock`. Read via `useMatches`, so it resolves in
 * MainLayout's own render pass — a view below the `<Outlet/>` cannot reach the
 * layout that hosts the panel.
 */
export interface ShogunRoutePlacement {
  mode: ShogunDockMode;
  forceOpen: boolean;
  /** The route renders its own chat, so the panel must not mount (app-shell ADR-020). */
  suppressed: boolean;
  /** Seeds a fresh thread's agent by name. A default, never a lock. */
  persona?: string;
}

interface RouteHandle {
  shogunDock?: unknown;
}

export function routePlacementFrom(
  matches: ReadonlyArray<{ handle?: unknown }>,
): ShogunRoutePlacement | null {
  // Innermost wins: a child route may override its layout's placement.
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle = matches[i]?.handle as RouteHandle | undefined;
    const declared = handle?.shogunDock;
    if (!declared || typeof declared !== 'object') continue;
    const spec = declared as {
      mode?: unknown;
      forceOpen?: unknown;
      suppressed?: unknown;
      persona?: unknown;
    };
    // A route whose own content IS the chat suppresses the panel, so exactly
    // one chat surface is ever mounted (app-shell ADR-020).
    if (spec.suppressed === true) return { mode: 'bottom', forceOpen: false, suppressed: true };
    if (!isMode(spec.mode)) continue;
    return {
      mode: spec.mode,
      forceOpen: spec.forceOpen === true,
      suppressed: false,
      ...(typeof spec.persona === 'string' ? { persona: spec.persona } : {}),
    };
  }
  return null;
}

export interface UseShogunDock {
  mode: ShogunDockMode;
  open: boolean;
  /** The route owns the chat itself; the panel must not mount (app-shell ADR-020). */
  suppressed: boolean;
  /** Route-supplied default agent name for a fresh thread, if any. */
  personaName?: string;
  floating: ShogunFloatingRect;
  docked: ShogunDockedSize;
  setFloating: (rect: ShogunFloatingRect) => void;
  commitFloating: () => void;
  setDocked: (size: ShogunDockedSize) => void;
  commitDocked: () => void;
}

/**
 * Resolve the route-supplied placement. Split out of `useShogunDock` because
 * `useMatches` requires a DATA router, and functional stories mount the dock
 * hook with no router at all.
 */
export function useShogunRoutePlacement(): ShogunRoutePlacement | null {
  const matches = useMatches();
  return useMemo(() => routePlacementFrom(matches), [matches]);
}

export function useShogunDock(routePlacement: ShogunRoutePlacement | null = null): UseShogunDock {
  const { data } = useUserPreferences();

  const [rectState, setRectState] = useAtom(shogunFloatingRectAtom);
  const [dockedState, setDockedState] = useAtom(shogunDockedSizeAtom);

  const storedMode: ShogunDockMode = isMode(data?.shogun_dock_mode)
    ? data.shogun_dock_mode
    : DEFAULT_MODE;
  const mode: ShogunDockMode = routePlacement?.mode ?? storedMode;

  useEffect(() => {
    if (rectState !== null) return;
    const { vw, vh } = viewport();
    setRectState(readGeometry(vw, vh) ?? defaultFloatingRect(vw, vh));
  }, [rectState, setRectState]);

  useEffect(() => {
    if (dockedState !== null) return;
    setDockedState(readDockedSize() ?? { width: DOCKED_W, height: DOCKED_H });
  }, [dockedState, setDockedState]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      setRectState((prev) => {
        if (prev === null) return prev;
        const { vw, vh } = viewport();
        const next = clampRect(prev, vw, vh);
        writeGeometry(next);
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setRectState]);

  const { vw, vh } = viewport();
  const floating = rectState ?? defaultFloatingRect(vw, vh);
  const docked = dockedState ?? { width: DOCKED_W, height: DOCKED_H };

  const setFloating = useCallback(
    (rect: ShogunFloatingRect) => {
      const { vw: w, vh: h } = viewport();
      setRectState(clampRect(rect, w, h));
    },
    [setRectState],
  );

  const commitFloating = useCallback(() => {
    setRectState((prev) => {
      if (prev !== null) writeGeometry(prev);
      return prev;
    });
  }, [setRectState]);

  const setDocked = useCallback(
    (size: ShogunDockedSize) => {
      setDockedState(clampDockedSize(size));
    },
    [setDockedState],
  );

  const commitDocked = useCallback(() => {
    setDockedState((prev) => {
      if (prev !== null) writeDockedSize(prev);
      return prev;
    });
  }, [setDockedState]);

  return {
    mode,
    open: routePlacement?.forceOpen === true,
    suppressed: routePlacement?.suppressed === true,
    ...(routePlacement?.persona ? { personaName: routePlacement.persona } : {}),
    floating,
    docked,
    setFloating,
    commitFloating,
    setDocked,
    commitDocked,
  };
}
